import React, { useState, useEffect } from 'react';
import { Search, Bot, Loader2, AlertCircle, Image as ImageIcon, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Assistant {
  id: string;
  nombre: string;
  descripcion: string;
  modelo: string;
  estado: string;
}

interface TavilyConfig {
  id: string;
  nombre: string;
  api_key: string;
  endpoint: string;
  query: string | null;
  topic: 'general' | 'news';
  search_depth: 'basic' | 'advanced';
  max_results: number;
  time_range: 'day' | 'week' | 'month' | 'year' | 'd' | 'w' | 'm' | 'y';
  include_answer: boolean;
  include_raw_content: boolean;
  include_images: boolean;
  include_image_descriptions: boolean;
  include_domains: string[];
  exclude_domains: string[];
  language: string;
}

interface SearchResult {
  title: string;
  url: string;
  content: string;
  score: number;
}

interface TavilyResponse {
  query: string;
  answer?: string;
  images?: Array<{
    url: string;
    description: string;
  }>;
  results: SearchResult[];
}

export default function TavilySearch() {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [configs, setConfigs] = useState<TavilyConfig[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<string>('');
  const [selectedConfig, setSelectedConfig] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [answer, setAnswer] = useState<string | null>(null);
  const [images, setImages] = useState<Array<{url: string; description: string}>>([]);
  const [selectedResults, setSelectedResults] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchAssistants();
    fetchConfigs();
  }, []);

  const fetchAssistants = async () => {
    try {
      const { data, error } = await supabase
        .from('asistentes')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setAssistants(data || []);
    } catch (error: any) {
      console.error('Error fetching assistants:', error);
      setError('Error al cargar los asistentes');
    }
  };

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('tavily_search_configs')
        .select('*')
        .order('nombre');

      if (error) throw error;
      setConfigs(data || []);
    } catch (error: any) {
      console.error('Error fetching configs:', error);
      setError('Error al cargar las configuraciones');
    }
  };

  const handleSearch = async () => {
    if (!selectedConfig || !searchQuery.trim()) {
      setError('Selecciona una configuración y escribe una búsqueda');
      return;
    }

    setIsLoading(true);
    setError('');
    setResults([]);
    setAnswer(null);
    setImages([]);

    try {
      const config = configs.find(c => c.id === selectedConfig);
      if (!config) throw new Error('Configuración no encontrada');

      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.api_key}`
        },
        body: JSON.stringify({
          query: searchQuery,
          topic: config.topic,
          search_depth: config.search_depth,
          max_results: config.max_results,
          time_range: config.time_range,
          include_answer: config.include_answer,
          include_raw_content: config.include_raw_content,
          include_images: config.include_images,
          include_image_descriptions: config.include_image_descriptions,
          include_domains: config.include_domains,
          exclude_domains: config.exclude_domains,
          language: config.language || 'es' // Usar español por defecto
        })
      });

      if (!response.ok) {
        throw new Error(`Error en la búsqueda: ${response.statusText}`);
      }

      const data: TavilyResponse = await response.json();
      setResults(data.results);
      setAnswer(data.answer || null);
      setImages(data.images || []);

    } catch (error: any) {
      console.error('Error performing search:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultSelect = (index: number) => {
    setSelectedResults(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSaveResults = async () => {
    if (!selectedAssistant || selectedResults.size === 0) {
      setError('Selecciona un asistente y al menos un resultado');
      return;
    }

    try {
      const config = configs.find(c => c.id === selectedConfig);
      if (!config) throw new Error('Configuración no encontrada');

      // Crear un array de registros para insertar
      const recordsToInsert = Array.from(selectedResults).map(index => ({
        asistente_id: selectedAssistant,
        query: searchQuery,
        answer: answer,
        images: images,
        search_params: {
          config_id: config.id,
          topic: config.topic,
          search_depth: config.search_depth,
          max_results: config.max_results,
          time_range: config.time_range,
          results: [results[index]], // Guardar solo el resultado específico
          language: config.language || 'es'
        }
      }));

      // Insertar todos los registros seleccionados
      const { error } = await supabase
        .from('tavily_search_results')
        .insert(recordsToInsert);

      if (error) throw error;

      setSelectedResults(new Set());
      setError('');
      alert(`${recordsToInsert.length} resultados guardados correctamente`);

    } catch (error: any) {
      console.error('Error saving results:', error);
      setError('Error al guardar los resultados');
    }
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Búsqueda web avanzada</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Configuración
          </label>
          <select
            value={selectedConfig}
            onChange={(e) => setSelectedConfig(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Seleccionar configuración</option>
            {configs.map((config) => (
              <option key={config.id} value={config.id}>
                {config.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Asistente
          </label>
          <select
            value={selectedAssistant}
            onChange={(e) => setSelectedAssistant(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Seleccionar asistente</option>
            {assistants.map((assistant) => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex space-x-4">
        <div className="flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Escribe tu búsqueda..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Buscando...</span>
            </>
          ) : (
            <>
              <Search className="w-5 h-5" />
              <span>Buscar</span>
            </>
          )}
        </button>
      </div>

      {answer && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-800 mb-2">Respuesta</h3>
          <p className="text-blue-900">{answer}</p>
        </div>
      )}

      {images.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 mb-2 flex items-center">
            <ImageIcon className="w-5 h-5 mr-2" />
            Imágenes encontradas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <img
                  src={image.url}
                  alt={image.description}
                  className="w-full h-48 object-cover"
                />
                <p className="p-2 text-sm text-gray-600">{image.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="flex-1 bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-800">Resultados de la búsqueda</h3>
            <button
              onClick={handleSaveResults}
              disabled={selectedResults.size === 0 || !selectedAssistant}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>Guardar Seleccionados ({selectedResults.size})</span>
            </button>
          </div>
          <div className="overflow-auto max-h-[500px]">
            {results.map((result, index) => (
              <div
                key={index}
                className={`p-4 border-b hover:bg-gray-50 ${
                  selectedResults.has(index) ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedResults.has(index)}
                    onChange={() => handleResultSelect(index)}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">
                      <a
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {result.title}
                      </a>
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">{result.content}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>Relevancia: {result.score.toFixed(2)}</span>
                      <a
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        {new URL(result.url).hostname}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}